const MONTHS_PER_YEAR = 12;
const LEGACY_PRODUCTION_SNAPSHOT_KEY = '_legacy_production_callback';
const INPUT_FINGERPRINT_RE = /^[a-f0-9]{64}$/i;
export const LEGACY_RUNTIME_PRODUCTION_SNAPSHOT_KEY =
  '_legacy_runtime_production_snapshot';
export const LEGACY_RUNTIME_PRODUCTION_SNAPSHOT_SOURCE =
  'latest_legacy_runtime_v1';
export const LEGACY_CANVAS_UNAVAILABLE_REASON = 'legacy_canvas_unavailable';

const LEGACY_PRODUCTION_RECALCULABLE_MISS_REASONS = new Set([
  'snapshot_missing',
  'input_drift',
]);

const legacyRuntimeSnapshotPostsInFlight = new Map();

const getLegacyProductionResponseData = (responseOrError) => {
  let responseData =
    responseOrError?.response?.data === undefined
      ? responseOrError
      : responseOrError.response.data;
  if (typeof responseData === 'string') {
    try {
      responseData = JSON.parse(responseData);
    } catch (e) {
      responseData = null;
    }
  }
  return responseData;
};

export const getLegacyProductionResponseReason = (responseOrError) => {
  const responseData = getLegacyProductionResponseData(responseOrError);

  const candidate =
    responseData?.reason ??
    responseOrError?.reason ??
    responseOrError?.code ??
    null;
  return typeof candidate === 'string' &&
    /^[a-z0-9_-]{1,80}$/i.test(candidate)
    ? candidate
    : null;
};

const parseDesignData = (designData) => {
  if (typeof designData !== 'string') return designData;

  try {
    return JSON.parse(designData);
  } catch (e) {
    return null;
  }
};

const isLegacyProductionSafetyRejection = (responseOrError) => {
  const responseData = getLegacyProductionResponseData(responseOrError);
  const explicitReason =
    responseData?.reason ?? responseOrError?.reason ?? null;
  if (typeof explicitReason === 'string') {
    return true;
  }

  const status = Number(responseOrError?.response?.status);
  return (
    Number.isInteger(status) &&
    status >= 400 &&
    status < 500 &&
    status !== 404 &&
    status !== 405
  );
};

const hasExplicitLegacyProvenance = (designData) => {
  const normalizedDesignData = parseDesignData(designData);
  if (!normalizedDesignData || typeof normalizedDesignData !== 'object') {
    return false;
  }

  const selectionMarker =
    normalizedDesignData._legacy_selection_migration;
  if (
    selectionMarker?.schema_version === 1 &&
    selectionMarker?.source === 'legacy_production_callback' &&
    INPUT_FINGERPRINT_RE.test(
      String(selectionMarker?.callback_sha256 || '')
    )
  ) {
    return true;
  }

  const callbackSnapshot =
    normalizedDesignData[LEGACY_PRODUCTION_SNAPSHOT_KEY];
  if (
    callbackSnapshot?.schema_version === 1 &&
    callbackSnapshot?.source === 'legacy_production_callback' &&
    INPUT_FINGERPRINT_RE.test(
      String(callbackSnapshot?.callback_sha256 || '')
    )
  ) {
    return true;
  }

  const runtimeSnapshot =
    normalizedDesignData[LEGACY_RUNTIME_PRODUCTION_SNAPSHOT_KEY];
  return Boolean(
    runtimeSnapshot?.schema_version === 1 &&
      runtimeSnapshot?.source ===
        LEGACY_RUNTIME_PRODUCTION_SNAPSHOT_SOURCE &&
      INPUT_FINGERPRINT_RE.test(
        String(runtimeSnapshot?.input_fingerprint || '')
      ) &&
      INPUT_FINGERPRINT_RE.test(
        String(runtimeSnapshot?.payload_sha256 || '')
      )
  );
};

export const createPendingLegacyProductionHydration = () => {
  let context = null;

  return {
    defer: (nextContext) => {
      context = nextContext || null;
      return context;
    },
    clear: () => {
      const cleared = context;
      context = null;
      return cleared;
    },
    claim: ({
      isLegacyProposal = false,
      canvasLoaded = false,
      isActive = () => true,
    } = {}) => {
      if (!context || !isLegacyProposal || !canvasLoaded) return null;
      if (typeof isActive === 'function' && !isActive(context)) {
        context = null;
        return null;
      }

      const claimed = context;
      context = null;
      return claimed;
    },
  };
};

export const shouldDeferLegacyProductionHydration = ({
  isLegacyProposal = false,
  reason = null,
  canvasLoaded = false,
} = {}) =>
  isLegacyProposal &&
  reason === LEGACY_CANVAS_UNAVAILABLE_REASON &&
  !canvasLoaded;

export const getLegacyRuntimeProductionInputFingerprint = (response) => {
  const fingerprint = response?.input_fingerprint;
  return typeof fingerprint === 'string' &&
    INPUT_FINGERPRINT_RE.test(fingerprint)
    ? fingerprint
    : null;
};

const finiteNumber = (value, fallback = 0) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
};

const finitePanelCount = (value) => Math.max(0, Math.trunc(finiteNumber(value)));

export const mergeLegacyRuntimeProductionSnapshot = ({
  designData = {},
  snapshot = null,
} = {}) => {
  const normalizedDesignData = parseDesignData(designData);
  if (!normalizedDesignData || typeof snapshot !== 'object' || !snapshot) {
    return normalizedDesignData || {};
  }

  return {
    ...normalizedDesignData,
    [LEGACY_RUNTIME_PRODUCTION_SNAPSHOT_KEY]: snapshot,
  };
};

const isSelectedValue = (value) => value === true || value === 1 || value === '1';

const sumAnnualUsage = (annualUsage) => {
  if (Array.isArray(annualUsage)) {
    return annualUsage.reduce((sum, value) => sum + finiteNumber(value), 0);
  }

  return finiteNumber(annualUsage);
};

const toLegacyMonthlyCallbackValues = (value) => {
  const monthlyValues = normalizeLegacyMonthlyValues(value);
  return [...monthlyValues, ...Array(Math.max(0, MONTHS_PER_YEAR - monthlyValues.length)).fill(0)]
    .slice(0, MONTHS_PER_YEAR)
    .map((entry) => finiteNumber(entry).toFixed(0));
};

const normalizeLegacySeries = (value, length, includeAnnual = false) => {
  if (Array.isArray(value)) {
    return value.slice(0, length).map((entry) => finiteNumber(entry));
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return [];

    if ((trimmed.startsWith('[') && trimmed.endsWith(']')) || (trimmed.startsWith('{') && trimmed.endsWith('}'))) {
      try {
        return normalizeLegacySeries(
          JSON.parse(trimmed),
          length,
          includeAnnual
        );
      } catch (e) {
        // Fall through to CSV/Postgres-array style parsing below.
      }
    }

    const csv = trimmed.startsWith('{') && trimmed.endsWith('}') ? trimmed.slice(1, -1) : trimmed;
    return csv
      .split(',')
      .slice(0, length)
      .map((entry) => finiteNumber(entry));
  }

  if (value && typeof value === 'object') {
    const hasKey = (key) => Object.prototype.hasOwnProperty.call(value, key);
    const zeroIndexedKeys = Array.from({ length }, (_, index) =>
      String(index)
    );
    const oneIndexedKeys = Array.from({ length }, (_, index) =>
      String(index + 1)
    );
    const monthNameKeys = [
      ['jan', 'january'],
      ['feb', 'february'],
      ['mar', 'march'],
      ['apr', 'april'],
      ['may'],
      ['jun', 'june'],
      ['jul', 'july'],
      ['aug', 'august'],
      ['sep', 'sept', 'september'],
      ['oct', 'october'],
      ['nov', 'november'],
      ['dec', 'december'],
    ];

    if (hasKey('0')) {
      return zeroIndexedKeys.map((key) => finiteNumber(value[key]));
    }

    if (oneIndexedKeys.some(hasKey)) {
      return oneIndexedKeys.map((key) => finiteNumber(value[key]));
    }

    const monthlyValues = monthNameKeys.map((keys) => {
      const foundKey = keys.find(hasKey);
      return foundKey ? finiteNumber(value[foundKey]) : 0;
    });
    if (!includeAnnual) return monthlyValues;

    const annualKey = [
      'annual',
      'annualAccess',
      'annual_access',
      'annualSolarAccess',
      'annual_solar_access',
    ].find(hasKey);
    return [
      ...monthlyValues,
      annualKey ? finiteNumber(value[annualKey]) : 0,
    ];
  }

  return [];
};

export const normalizeLegacyMonthlyValues = (value) =>
  normalizeLegacySeries(value, MONTHS_PER_YEAR);

export const normalizeLegacySolarAccessValues = (value) =>
  normalizeLegacySeries(value, MONTHS_PER_YEAR + 1, true);

export const isPersistedLegacyProposal = ({ proposalVersion, designData, designs = [], legacyVersion = '0.0.0' } = {}) => {
  const normalizedDesignData = parseDesignData(designData);

  const legacyKeys = [
    'panel',
    'trees',
    'losses',
    'panels',
    'inverter',
    'mounting',
    'segments',
    'obstacles',
    'panelType',
    'monitoring',
  ];
  const hasLegacyDesignDataShape =
    normalizedDesignData &&
    typeof normalizedDesignData === 'object' &&
    legacyKeys.some((key) => Object.prototype.hasOwnProperty.call(normalizedDesignData, key)) &&
    Array.isArray(normalizedDesignData.segments) &&
    Array.isArray(normalizedDesignData.panels);

  if (!hasLegacyDesignDataShape) return false;

  const normalizedDesigns = Array.isArray(designs) ? designs : [];
  const hasLegacyMonthlyProduction = normalizedDesigns.some((design) =>
    normalizeLegacyMonthlyValues(design?.ac_monthly).some((value) => finiteNumber(value) > 0)
  );
  const hasModernAnnualProduction = normalizedDesigns.some(
    (design) => finiteNumber(design?.annual_kwh) > 0 || finiteNumber(design?.clipping_kwh) > 0
  );
  const hasModernProductionData =
    finiteNumber(normalizedDesignData?.total_annual_kwh) > 0 ||
    finiteNumber(normalizedDesignData?.summary?.solar_production) > 0 ||
    finiteNumber(normalizedDesignData?.summary?.solarProduction) > 0 ||
    Array.isArray(normalizedDesignData?.production_per_panel) ||
    Array.isArray(normalizedDesignData?.productionPerPanel) ||
    Boolean(normalizedDesignData?.selectedPanelsPerRoof) ||
    Boolean(normalizedDesignData?.selected_panels_per_roof) ||
    Boolean(normalizedDesignData?.design_id);
  const isLegacyVersion = proposalVersion === legacyVersion || (!proposalVersion && !hasModernProductionData);
  const designsMissingProduction =
    normalizedDesigns.length > 0 &&
    normalizedDesigns.every(
      (design) => finiteNumber(design?.annual_kwh) <= 0 && finiteNumber(design?.clipping_kwh) <= 0 && !design?.stringing_data
    );

  // Migration stamped some legacy proposals with the current frontend version and
  // added stringing metadata, while their production still lives only in ac_monthly.
  const hasMigratedLegacyProduction =
    normalizedDesigns.length > 0 &&
    hasLegacyMonthlyProduction &&
    !hasModernAnnualProduction &&
    hasExplicitLegacyProvenance(normalizedDesignData);

  return (isLegacyVersion && (designsMissingProduction || hasLegacyMonthlyProduction)) || hasMigratedLegacyProduction;
};

export const getLegacyRoofPanelCount = (roof = {}) => {
  if (Array.isArray(roof?.panels)) return roof.panels.length;

  const vertical = finitePanelCount(roof?.vertical);
  const horizontal = finitePanelCount(roof?.horizontal);
  return vertical + horizontal;
};

export const buildEmptyLegacySelection = (design = []) => {
  if (!Array.isArray(design)) return {};

  return design.reduce((selection, roof) => {
    if (roof?.id) {
      selection[roof.id] = Array(getLegacyRoofPanelCount(roof)).fill(0);
    }
    return selection;
  }, {});
};

const countSelectedValues = (values) =>
  Array.isArray(values)
    ? values.reduce(
      (count, value) => count + (finiteNumber(value) > 0 ? 1 : 0),
      0
    )
    : 0;

const legacyDesignIndex = (value) => {
  const match = String(value || '').match(/-(\d{3})$/);
  return match ? Number(match[1]) : null;
};

export const buildLegacyHourlyPanelTopology = ({
  design = [],
  designData = null,
  selectedPanels = {},
} = {}) => {
  const normalizedDesignData = parseDesignData(designData);
  const segments = Array.isArray(normalizedDesignData?.segments)
    ? normalizedDesignData.segments
    : [];
  if (segments.length === 0) return [];

  const indexesBySegmentId = new Map();
  segments.forEach((segment, index) => {
    if (segment?.id === null || segment?.id === undefined) return;
    const segmentId = String(segment.id);
    const indexes = indexesBySegmentId.get(segmentId) || [];
    indexes.push(index);
    indexesBySegmentId.set(segmentId, indexes);
  });
  const uniqueIndexForSegmentId = (value) => {
    if (value === null || value === undefined || value === '') return null;
    const indexes = indexesBySegmentId.get(String(value)) || [];
    return indexes.length === 1 ? indexes[0] : null;
  };

  const designMap =
    normalizedDesignData?.design_map || normalizedDesignData?.designMap || {};
  const panelGroups = Array.isArray(normalizedDesignData?.panels)
    ? normalizedDesignData.panels
    : [];
  const topology = segments.map((segment, segmentIndex) => {
    const segmentId = segment?.id;
    const isUniqueId =
      segmentId !== null &&
      segmentId !== undefined &&
      (indexesBySegmentId.get(String(segmentId)) || []).length === 1;
    return {
      ...(isUniqueId ? { segment_id: segmentId } : {}),
      segment_index: segmentIndex,
      selected_panels: 0,
    };
  });
  const assignedSegmentIndexes = new Set();

  const assignSelection = (roofId, values, roofIndex = null, roof = null) => {
    const mappedSegmentId =
      roofId !== null && roofId !== undefined && designMap
        ? designMap[roofId]
        : null;
    const explicitSegmentId = roof?.segment_id ?? roof?.segmentId ?? null;
    const suffixIndex = legacyDesignIndex(roofId);
    const panelGroupId =
      roofIndex !== null && panelGroups[roofIndex]
        ? panelGroups[roofIndex]?.id
        : null;
    const candidates = [explicitSegmentId, mappedSegmentId];
    let segmentIndex = null;
    for (const candidate of candidates) {
      segmentIndex = uniqueIndexForSegmentId(candidate);
      if (segmentIndex !== null) break;
    }
    if (
      segmentIndex === null &&
      Number.isInteger(suffixIndex) &&
      suffixIndex >= 0 &&
      suffixIndex < segments.length
    ) {
      segmentIndex = suffixIndex;
    }
    if (segmentIndex === null) {
      segmentIndex = uniqueIndexForSegmentId(panelGroupId);
    }
    if (
      segmentIndex === null &&
      Number.isInteger(roofIndex) &&
      roofIndex >= 0 &&
      roofIndex < segments.length
    ) {
      segmentIndex = roofIndex;
    }
    if (segmentIndex === null) {
      throw new Error(
        `Legacy roof ${String(roofId)} cannot be mapped to a segment`
      );
    }
    if (assignedSegmentIndexes.has(segmentIndex)) {
      throw new Error(
        `Legacy segment ${segmentIndex} has duplicate panel selections`
      );
    }

    topology[segmentIndex].selected_panels = countSelectedValues(values);
    assignedSegmentIndexes.add(segmentIndex);
  };

  const normalizedDesign = Array.isArray(design) ? design : [];
  const mappedRoofIds = new Set();
  normalizedDesign.forEach((roof, roofIndex) => {
    const roofId = roof?.id;
    if (roofId === null || roofId === undefined) return;
    mappedRoofIds.add(String(roofId));
    assignSelection(roofId, selectedPanels?.[roofId], roofIndex, roof);
  });

  if (selectedPanels && typeof selectedPanels === 'object') {
    Object.entries(selectedPanels).forEach(([roofId, values]) => {
      if (mappedRoofIds.has(String(roofId))) return;
      assignSelection(roofId, values);
    });
  }

  return topology;
};

const getLegacyJsonSelection = ({ design = [], designData = null } = {}) => {
  const sourceRoofs = parseDesignData(designData)?.panels;
  if (!Array.isArray(design) || !Array.isArray(sourceRoofs)) return null;

  const selectedPanels = buildEmptyLegacySelection(design);
  let totalPanels = 0;
  design.forEach((roof, roofIndex) => {
    if (!roof?.id) return;
    const sourceRoof = sourceRoofs[roofIndex] || {};
    const verticalSelection = Array.isArray(sourceRoof?.vertical?.selected)
      ? sourceRoof.vertical.selected
      : [];
    const horizontalSelection = Array.isArray(sourceRoof?.horizontal?.selected)
      ? sourceRoof.horizontal.selected
      : [];
    const sourceSelection = [
      ...verticalSelection,
      ...horizontalSelection,
    ];
    selectedPanels[roof.id] = selectedPanels[roof.id].map((_, panelIndex) =>
      isSelectedValue(sourceSelection[panelIndex]) ? 1 : 0
    );
    totalPanels += selectedPanels[roof.id].reduce(
      (sum, value) => sum + value,
      0
    );
  });

  return totalPanels > 0 ? { selectedPanels, totalPanels } : null;
};

const selectionsMatch = (design, left, right) =>
  design.every((roof) => {
    const leftValues = left?.[roof?.id] || [];
    const rightValues = right?.[roof?.id] || [];
    return (
      leftValues.length === rightValues.length &&
      leftValues.every((value, index) => value === rightValues[index])
    );
  });

const hasGeneratedModernSelection = (designData) => {
  const normalizedDesignData = parseDesignData(designData);
  if (!normalizedDesignData || typeof normalizedDesignData !== 'object') return false;

  return (
    finiteNumber(normalizedDesignData.selected_panels) > 0 ||
    finiteNumber(normalizedDesignData.selectedPanels) > 0 ||
    Boolean(normalizedDesignData.panel_metrics) ||
    Boolean(normalizedDesignData.panelMetrics) ||
    Boolean(normalizedDesignData.selectedPackage) ||
    Boolean(normalizedDesignData.stringing_data) ||
    Boolean(normalizedDesignData.stringingData) ||
    Array.isArray(normalizedDesignData.productionPerPanel) ||
    Array.isArray(normalizedDesignData.production_per_panel)
  );
};

const hasVerifiedLegacySelectionImport = (designData, selectedCount) => {
  const normalizedDesignData = parseDesignData(designData);
  const marker = normalizedDesignData?._legacy_selection_migration;
  if (!marker || typeof marker !== 'object') return false;

  const canonicalFields = Array.isArray(marker.canonical_fields)
    ? marker.canonical_fields
    : [];
  const markerSelectedCount = Number(marker.selected_panel_count);
  return (
    Number(marker.schema_version) === 1 &&
    marker.source === 'legacy_production_callback' &&
    /^[a-f0-9]{64}$/i.test(String(marker.callback_sha256 || '')) &&
    Number.isInteger(markerSelectedCount) &&
    markerSelectedCount >= 0 &&
    markerSelectedCount === selectedCount &&
    canonicalFields.includes('designs.vertical_selected') &&
    canonicalFields.includes('designs.horizontal_selected')
  );
};

const isValidLegacyProductionPayload = ({
  design = [],
  selection = null,
  payload = null,
} = {}) => {
  if (!selection || !payload || typeof payload !== 'object') return false;

  const totalPanelCount = design.reduce(
    (sum, roof) => sum + getLegacyRoofPanelCount(roof),
    0
  );
  if (
    finitePanelCount(payload.numSelectedPanels) !== selection.totalPanels ||
    finitePanelCount(payload.numTotalPanels) !== totalPanelCount ||
    !Number.isFinite(Number(payload.offset)) ||
    !Number.isFinite(Number(payload.systemSize)) ||
    Number(payload.systemSize) < 0 ||
    !Number.isFinite(Number(payload.yearlyProduction)) ||
    Number(payload.yearlyProduction) < 0 ||
    !Array.isArray(payload.monthlyProduction) ||
    payload.monthlyProduction.length !== MONTHS_PER_YEAR ||
    payload.monthlyProduction.some(
      (value) => !Number.isFinite(Number(value)) || Number(value) < 0
    ) ||
    !Array.isArray(payload.productionPerPanel) ||
    !Array.isArray(payload.pitchAndAzimuthPerRoof) ||
    !Array.isArray(payload.shadingPerRoof)
  ) {
    return false;
  }

  const productionByRoof = new Map();
  for (const entry of payload.productionPerPanel) {
    const roofIndex = Number(entry?.idx);
    if (
      !Number.isInteger(roofIndex) ||
      roofIndex < 0 ||
      roofIndex >= design.length ||
      productionByRoof.has(roofIndex) ||
      !Array.isArray(entry?.roofPanels)
    ) {
      return false;
    }
    productionByRoof.set(roofIndex, entry.roofPanels);
  }

  for (let roofIndex = 0; roofIndex < design.length; roofIndex += 1) {
    const roof = design[roofIndex];
    const roofPanelCount = getLegacyRoofPanelCount(roof);
    const production = productionByRoof.get(roofIndex) || [];
    if (
      production.length > roofPanelCount ||
      production.some(
        (value) => !Number.isFinite(Number(value)) || Number(value) < 0
      )
    ) {
      return false;
    }
    const expected = selection.selectedPanels?.[roof?.id] || [];
    for (let panelIndex = 0; panelIndex < roofPanelCount; panelIndex += 1) {
      if (
        Number(production[panelIndex] || 0) > 0 &&
        Number(expected[panelIndex] || 0) <= 0
      ) {
        return false;
      }
    }
  }

  return true;
};

export const getVerifiedLegacyProductionSnapshot = ({
  design = [],
  designData = null,
  selectedPanels = {},
} = {}) => {
  const normalizedDesignData = parseDesignData(designData);
  const selection = normalizeLegacySelectedPanels(design, selectedPanels);
  if (!selection) return null;
  if (!hasVerifiedLegacySelectionImport(designData, selection.totalPanels)) {
    return null;
  }

  const marker = normalizedDesignData?._legacy_selection_migration;
  const snapshot = normalizedDesignData?.[LEGACY_PRODUCTION_SNAPSHOT_KEY];
  const payload = snapshot?.data;
  if (
    !snapshot ||
    typeof snapshot !== 'object' ||
    Number(snapshot.schema_version) !== 1 ||
    snapshot.source !== 'legacy_production_callback' ||
    snapshot.callback_sha256 !== marker.callback_sha256 ||
    !payload ||
    typeof payload !== 'object'
  ) {
    return null;
  }

  return isValidLegacyProductionPayload({ design, selection, payload })
    ? payload
    : null;
};

export const normalizeLegacyRuntimeProductionSnapshot = ({
  design = [],
  snapshot = null,
} = {}) => {
  const candidate = snapshot?.snapshot || snapshot;
  if (!candidate || typeof candidate !== 'object') return null;

  const responseFingerprint =
    getLegacyRuntimeProductionInputFingerprint(snapshot);
  if (
    !responseFingerprint ||
    candidate.input_fingerprint !== responseFingerprint
  ) {
    return null;
  }

  const source = candidate.source ?? candidate.provenance;
  if (
    Number(candidate.schema_version) !== 1 ||
    !INPUT_FINGERPRINT_RE.test(String(candidate.payload_sha256 || '')) ||
    source !== LEGACY_RUNTIME_PRODUCTION_SNAPSHOT_SOURCE ||
    (candidate.source &&
      candidate.source !== LEGACY_RUNTIME_PRODUCTION_SNAPSHOT_SOURCE) ||
    (candidate.provenance &&
      candidate.provenance !== LEGACY_RUNTIME_PRODUCTION_SNAPSHOT_SOURCE)
  ) {
    return null;
  }

  const selection = normalizeLegacySelectedPanels(
    design,
    candidate.selected_panels
  );
  if (
    !selection ||
    !isValidLegacyProductionPayload({
      design,
      selection,
      payload: candidate.data,
    })
  ) {
    return null;
  }

  return {
    data: candidate.data,
    selectedPanels: selection.selectedPanels,
    totalPanels: selection.totalPanels,
    snapshot: candidate,
  };
};

const runLazyLegacyProductionSnapshotFlow = async ({
  authoritativeSnapshot = null,
  getSnapshot,
  normalizeSnapshot,
  hydrateSnapshot,
  calculate,
  buildCallbackPayload,
  getSelectedPanels,
  postSnapshot,
  applyPersistedSnapshot,
  emit,
  onSnapshotReadError,
  onSnapshotPersistenceError,
  proposalId,
  isActive = () => true,
}) => {
  const flowIsActive = () =>
    typeof isActive !== 'function' || Boolean(isActive());
  const superseded = () => ({ handled: false, source: 'superseded' });
  const hydrateAndEmit = async (snapshot, hydrateSource, source) => {
    if (!flowIsActive()) return superseded();
    await hydrateSnapshot(snapshot, hydrateSource);
    if (!flowIsActive()) return superseded();
    emit(snapshot.data);
    return { handled: true, source };
  };
  const resolveConflictWinner = async (reason) => {
    let winnerResponse = null;
    try {
      if (!flowIsActive()) return superseded();
      winnerResponse = await getSnapshot();
      if (!flowIsActive()) return superseded();
    } catch (error) {
      if (!flowIsActive()) return superseded();
      onSnapshotReadError?.(error);
    }

    const winnerFingerprint =
      getLegacyRuntimeProductionInputFingerprint(winnerResponse);
    const winnerSnapshot = winnerFingerprint
      ? normalizeSnapshot(winnerResponse)
      : null;
    if (winnerSnapshot) {
      return hydrateAndEmit(winnerSnapshot, 'runtime', 'conflict-winner');
    }
    return { handled: false, source: 'conflict', reason };
  };

  if (!flowIsActive()) return superseded();
  if (authoritativeSnapshot) {
    return hydrateAndEmit(
      authoritativeSnapshot,
      'authoritative',
      'authoritative'
    );
  }

  let snapshotResponse = null;
  let inputFingerprint = null;
  let runtimeSnapshot = null;
  let snapshotReadSucceeded = false;
  try {
    snapshotResponse = await getSnapshot();
    snapshotReadSucceeded = true;
    if (!flowIsActive()) return superseded();
    inputFingerprint =
      getLegacyRuntimeProductionInputFingerprint(snapshotResponse);
    if (inputFingerprint) {
      runtimeSnapshot = normalizeSnapshot(snapshotResponse);
    }
  } catch (error) {
    if (!flowIsActive()) return superseded();
    onSnapshotReadError?.(error);
    if (isLegacyProductionSafetyRejection(error)) {
      return {
        handled: false,
        source: 'conflict',
        reason:
          getLegacyProductionResponseReason(error) ||
          'snapshot_read_rejected',
      };
    }
  }

  if (runtimeSnapshot) {
    return hydrateAndEmit(runtimeSnapshot, 'runtime', 'runtime');
  }

  const snapshotReason =
    getLegacyProductionResponseReason(snapshotResponse);
  const isRecalculableSnapshotMiss =
    snapshotReadSucceeded &&
    inputFingerprint &&
    LEGACY_PRODUCTION_RECALCULABLE_MISS_REASONS.has(snapshotReason);
  if (snapshotReadSucceeded && !isRecalculableSnapshotMiss) {
    return {
      handled: false,
      source: 'conflict',
      reason: snapshotReason || 'invalid_snapshot_response',
    };
  }

  if (!flowIsActive()) return superseded();
  const calculated = await calculate();
  if (!flowIsActive()) return superseded();
  if (!calculated) return { handled: false, source: 'calculation' };

  if (!flowIsActive()) return superseded();
  const callbackPayload = buildCallbackPayload();
  if (!flowIsActive()) return superseded();
  const request = {
    data: callbackPayload,
    selected_panels: getSelectedPanels(),
    expected_input_fingerprint: inputFingerprint,
  };
  if (!request.expected_input_fingerprint) {
    if (!flowIsActive()) return superseded();
    emit(callbackPayload);
    return { handled: true, source: 'calculated', callbackPayload };
  }
  const postKey = `${String(proposalId || 'legacy-proposal')}:${JSON.stringify(
    request
  )}`;

  try {
    let postPromise = legacyRuntimeSnapshotPostsInFlight.get(postKey);
    if (!postPromise) {
      if (!flowIsActive()) return superseded();
      postPromise = Promise.resolve(postSnapshot(request));
      legacyRuntimeSnapshotPostsInFlight.set(postKey, postPromise);
      postPromise.then(
        () => {
          if (legacyRuntimeSnapshotPostsInFlight.get(postKey) === postPromise) {
            legacyRuntimeSnapshotPostsInFlight.delete(postKey);
          }
        },
        () => {
          if (legacyRuntimeSnapshotPostsInFlight.get(postKey) === postPromise) {
            legacyRuntimeSnapshotPostsInFlight.delete(postKey);
          }
        }
      );
    }

    const persistedResponse = await postPromise;
    if (!flowIsActive()) return superseded();
    if (isLegacyProductionSafetyRejection(persistedResponse)) {
      return resolveConflictWinner(
        getLegacyProductionResponseReason(persistedResponse)
      );
    }
    const persistedSnapshot =
      getLegacyRuntimeProductionInputFingerprint(persistedResponse) ===
      inputFingerprint
        ? normalizeSnapshot(persistedResponse)
        : null;
    if (persistedSnapshot) {
      if (!flowIsActive()) return superseded();
      await applyPersistedSnapshot(persistedSnapshot);
      if (!flowIsActive()) return superseded();
    } else {
      return resolveConflictWinner(
        getLegacyProductionResponseReason(persistedResponse) ||
          'invalid_snapshot_response'
      );
    }
  } catch (error) {
    if (!flowIsActive()) return superseded();
    onSnapshotPersistenceError?.(error);
    if (isLegacyProductionSafetyRejection(error)) {
      return resolveConflictWinner(
        getLegacyProductionResponseReason(error) ||
          'snapshot_write_rejected'
      );
    }
  }

  if (!flowIsActive()) return superseded();
  emit(callbackPayload);
  return { handled: true, source: 'calculated', callbackPayload };
};

export const createLazyLegacyProductionSnapshotFlow = () => {
  const runs = new Map();

  return (options = {}) => {
    if (!options.isLegacyProposal) {
      return Promise.resolve({ handled: false, source: 'non-legacy' });
    }

    const proposalKey = String(options.proposalId || 'legacy-proposal');
    if (!runs.has(proposalKey)) {
      const run = runLazyLegacyProductionSnapshotFlow(options);
      runs.set(proposalKey, run);
      run.then(
        (result) => {
          if (!result?.handled && runs.get(proposalKey) === run) {
            runs.delete(proposalKey);
          }
        },
        () => {
          if (runs.get(proposalKey) === run) {
            runs.delete(proposalKey);
          }
        }
      );
    }
    return runs.get(proposalKey);
  };
};

export const resolveLegacySelection = ({
  design = [],
  designData = null,
  persistedSelectedPanels = {},
  proposalVersion = null,
  legacyVersion = '0.0.0',
} = {}) => {
  const persistedSelection = normalizeLegacySelectedPanels(design, persistedSelectedPanels);
  const jsonSelection = getLegacyJsonSelection({ design, designData });
  const isCurrentStamped = Boolean(proposalVersion && proposalVersion !== legacyVersion);
  const hasVerifiedImport = Boolean(
    persistedSelection &&
      hasVerifiedLegacySelectionImport(
        designData,
        persistedSelection.totalPanels
      )
  );
  const copiedPartialJsonSelection = Boolean(
    persistedSelection &&
      jsonSelection &&
      selectionsMatch(
        design,
        persistedSelection.selectedPanels,
        jsonSelection.selectedPanels
      )
  );

  // Current frontend output can populate designs.*_selected for a migrated
  // source-blank proposal. Those values are derived output, not legacy input.
  if (
    persistedSelection &&
    (hasVerifiedImport ||
      (!copiedPartialJsonSelection &&
        !(isCurrentStamped && hasGeneratedModernSelection(designData))))
  ) {
    return {
      ...persistedSelection,
      source: hasVerifiedImport
        ? 'legacy-callback-import'
        : 'legacy-design-columns',
      requiresRuntimeSelection: false,
    };
  }

  return {
    selectedPanels: buildEmptyLegacySelection(design),
    totalPanels: 0,
    source: 'runtime',
    requiresRuntimeSelection: true,
  };
};

export const getLegacyHydrationPlan = ({ isLegacyProposal = false, ...selectionInputs } = {}) => {
  if (!isLegacyProposal) {
    return {
      mode: 'modern',
      source: 'modern',
      selectedPanels: selectionInputs.persistedSelectedPanels || {},
      totalPanels: 0,
      requiresRuntimeSelection: false,
    };
  }

  const selection = resolveLegacySelection(selectionInputs);
  return {
    ...selection,
    mode: selection.requiresRuntimeSelection ? 'legacy-runtime' : 'legacy-exact',
  };
};

const listLength = (value) => (Array.isArray(value) ? value.length : 0);

const getPanelGroupCount = (panelGroup, orientation, fallback = 0) => {
  const oriented = panelGroup?.[orientation] || {};
  const pointsLength = listLength(oriented.points);
  if (pointsLength > 0) return pointsLength;

  const selectedLength = listLength(oriented.selected);
  if (selectedLength > 0) return selectedLength;

  return finitePanelCount(fallback);
};

const getLegacyPanelGroupForRoof = ({ design = [], designData = null, roofIndex = 0 } = {}) => {
  const normalizedDesignData = parseDesignData(designData);
  const panelGroups = normalizedDesignData?.panels;
  if (!panelGroups) return null;

  const roof = Array.isArray(design) ? design[roofIndex] : null;
  const roofId = roof?.id;
  const designMap = normalizedDesignData?.design_map || normalizedDesignData?.designMap;
  const mappedPanelGroupId =
    roofId && designMap && typeof designMap === 'object'
      ? designMap[roofId]
      : null;

  if (mappedPanelGroupId !== null && mappedPanelGroupId !== undefined) {
    if (Array.isArray(panelGroups)) {
      return panelGroups.find((group) => String(group?.id) === String(mappedPanelGroupId)) || null;
    }
    return panelGroups?.[mappedPanelGroupId] || null;
  }

  if (Array.isArray(panelGroups)) {
    if (roofId !== null && roofId !== undefined) {
      const byId = panelGroups.find((group) => String(group?.id) === String(roofId));
      if (byId) return byId;
    }
    return panelGroups[roofIndex] || null;
  }

  return roofId && panelGroups?.[roofId] ? panelGroups[roofId] : null;
};

const getLegacyPanelIndexFromCanvasIndex = ({ panelGroup = null, roof = {}, panelIndex = null } = {}) => {
  if (panelIndex === null || panelIndex === undefined || panelIndex === '') return null;
  const canvasPanelIndex = finitePanelCount(panelIndex);
  const horizontalCount = getPanelGroupCount(panelGroup, 'horizontal', roof?.horizontal);
  const verticalCount = getPanelGroupCount(panelGroup, 'vertical', roof?.vertical);

  if (horizontalCount <= 0 && verticalCount <= 0) return null;
  if (
    canvasPanelIndex >= horizontalCount &&
    canvasPanelIndex < horizontalCount + verticalCount
  ) {
    return canvasPanelIndex - horizontalCount;
  }
  if (canvasPanelIndex >= 0 && canvasPanelIndex < horizontalCount) {
    return canvasPanelIndex + verticalCount;
  }
  return null;
};

export const normalizeLegacyPanelShadingOrder = (panelShadings = [], { design = [], designData = null } = {}) => {
  if (!Array.isArray(panelShadings)) return [];

  // Legacy Canvas indexed vertical panels before horizontal panels. The
  // current canvas emits the opposite order, so remap only for legacy math.
  const orientationRank = {
    vertical: 0,
    horizontal: 1,
  };
  const entries = panelShadings
    .filter(Array.isArray)
    .map((entry, sourceIndex) => {
      const roofIndex = finitePanelCount(entry[0]);
      const panelGroup = getLegacyPanelGroupForRoof({
        design,
        designData,
        roofIndex,
      });
      const mappedPanelIndex = getLegacyPanelIndexFromCanvasIndex({
        panelGroup,
        roof: Array.isArray(design) ? design[roofIndex] : null,
        panelIndex: entry[2],
      });
      return { entry, sourceIndex, roofIndex, mappedPanelIndex };
    });

  const hasCompleteCanvasIndexMap =
    entries.length > 0 &&
    entries.every(({ mappedPanelIndex }) => Number.isInteger(mappedPanelIndex)) &&
    new Set(entries.map(({ roofIndex, mappedPanelIndex }) => `${roofIndex}:${mappedPanelIndex}`)).size === entries.length;

  if (hasCompleteCanvasIndexMap) {
    return entries
      .sort((left, right) => {
        if (left.roofIndex !== right.roofIndex) return left.roofIndex - right.roofIndex;
        if (left.mappedPanelIndex !== right.mappedPanelIndex) {
          return left.mappedPanelIndex - right.mappedPanelIndex;
        }
        return left.sourceIndex - right.sourceIndex;
      })
      .map(({ entry, mappedPanelIndex }) => [
        entry[0],
        entry[1],
        mappedPanelIndex,
        ...entry.slice(3),
      ]);
  }

  const ordered = entries
    .sort((left, right) => {
      const leftRoof = left.roofIndex;
      const rightRoof = right.roofIndex;
      if (leftRoof !== rightRoof) return leftRoof - rightRoof;

      const leftRank = orientationRank[left.entry[1]] ?? 2;
      const rightRank = orientationRank[right.entry[1]] ?? 2;
      if (leftRank !== rightRank) return leftRank - rightRank;

      return left.sourceIndex - right.sourceIndex;
    });
  const nextPanelIndexByRoof = {};

  return ordered.map(({ entry }) => {
    const roofIndex = finitePanelCount(entry[0]);
    const panelIndex = nextPanelIndexByRoof[roofIndex] || 0;
    nextPanelIndexByRoof[roofIndex] = panelIndex + 1;
    return [entry[0], entry[1], panelIndex, ...entry.slice(3)];
  });
};

export const normalizeLegacySelectedPanels = (design = [], selectedPanels = {}) => {
  if (!Array.isArray(design) || !selectedPanels || typeof selectedPanels !== 'object') {
    return null;
  }

  const nextSelectedPanels = { ...selectedPanels };
  let totalPanels = 0;

  design.forEach((roof) => {
    const id = roof?.id;
    const totalRoofPanels = getLegacyRoofPanelCount(roof);
    if (!id || totalRoofPanels <= 0) return;

    const existingSelection = Array.isArray(selectedPanels?.[id]) ? selectedPanels[id] : [];
    const roofSelection = Array(totalRoofPanels)
      .fill(0)
      .map((_, index) => (finiteNumber(existingSelection[index]) > 0 ? 1 : 0));
    const selectedCount = roofSelection.reduce((sum, value) => sum + (value > 0 ? 1 : 0), 0);

    if (selectedCount <= 0) return;

    nextSelectedPanels[id] = roofSelection;
    totalPanels += selectedCount;
  });

  if (totalPanels <= 0) return null;

  return {
    selectedPanels: nextSelectedPanels,
    totalPanels,
  };
};

export const getPersistedMonthlyProduction = (persistedDesigns = []) => {
  if (!Array.isArray(persistedDesigns)) return null;

  const monthlyProduction = new Array(MONTHS_PER_YEAR).fill(0);
  let hasProduction = false;

  persistedDesigns.forEach((roof) => {
    const acMonthly = normalizeLegacyMonthlyValues(roof?.ac_monthly ?? roof?.acMonthly);

    acMonthly.slice(0, MONTHS_PER_YEAR).forEach((value, month) => {
      const production = finiteNumber(value);
      monthlyProduction[month] += production;
      if (production > 0) hasProduction = true;
    });
  });

  return hasProduction ? monthlyProduction : null;
};

const getPersistedRoof = (persistedDesigns = [], id = null, roofIndex = 0) => {
  const safePersistedDesigns = Array.isArray(persistedDesigns) ? persistedDesigns : [];
  const byId = id ? safePersistedDesigns.find((roof) => roof?.id === id) : null;
  return byId || safePersistedDesigns[roofIndex] || null;
};

const firstDefined = (...values) =>
  values.find(
    (value) => value !== undefined && value !== null && value !== ''
  );

const normalizeLegacySolarAccessFactor = (value) => {
  const number = Number(value);
  if (!Number.isFinite(number)) return null;

  const factor = number > 1 ? number / 100 : number;
  return Math.max(0, Math.min(1, factor));
};

const getExplicitLegacySeriesAnnualValue = (value) => {
  if (Array.isArray(value)) {
    return value.length > MONTHS_PER_YEAR
      ? value[MONTHS_PER_YEAR]
      : undefined;
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return undefined;
    if (
      (trimmed.startsWith('[') && trimmed.endsWith(']')) ||
      (trimmed.startsWith('{') && trimmed.endsWith('}'))
    ) {
      try {
        return getExplicitLegacySeriesAnnualValue(JSON.parse(trimmed));
      } catch {
        // Fall through for Postgres-array strings such as {0.8,0.9}.
      }
    }

    const csv =
      trimmed.startsWith('{') && trimmed.endsWith('}')
        ? trimmed.slice(1, -1)
        : trimmed;
    const values = csv.split(',');
    return values.length > MONTHS_PER_YEAR
      ? values[MONTHS_PER_YEAR]
      : undefined;
  }

  if (value && typeof value === 'object') {
    const annualKey = [
      'annual',
      'annualAccess',
      'annual_access',
      'annualSolarAccess',
      'annual_solar_access',
    ].find((key) => Object.prototype.hasOwnProperty.call(value, key));
    if (annualKey) return value[annualKey];
    if (Object.prototype.hasOwnProperty.call(value, '0')) {
      return Object.prototype.hasOwnProperty.call(value, '12')
        ? value[12]
        : undefined;
    }
    return Object.prototype.hasOwnProperty.call(value, '13')
      ? value[13]
      : undefined;
  }

  return undefined;
};

const getLegacyRoofSolarAccess = ({
  roof = {},
  persistedDesigns = [],
  roofIndex = 0,
} = {}) => {
  const persistedRoof = getPersistedRoof(
    persistedDesigns,
    roof?.id,
    roofIndex
  );
  const solarAccessCandidates = [
    roof?.solarAccess,
    roof?.solar_access,
    persistedRoof?.solar_access,
    persistedRoof?.solarAccess,
  ];
  const rawSolarAccess =
    solarAccessCandidates.find(
      (value) => normalizeLegacySolarAccessValues(value).length > 0
    ) ?? firstDefined(...solarAccessCandidates);
  const normalizedSeries = normalizeLegacySolarAccessValues(rawSolarAccess);
  const rawMonthly = normalizedSeries.slice(0, MONTHS_PER_YEAR);
  const explicitAnnual = firstDefined(
    roof?.annualAccess,
    roof?.annual_access,
    roof?.annualSolarAccess,
    roof?.annual_solar_access,
    persistedRoof?.annual_access,
    persistedRoof?.annualAccess,
    persistedRoof?.annual_solar_access,
    persistedRoof?.annualSolarAccess,
    getExplicitLegacySeriesAnnualValue(rawSolarAccess)
  );
  const normalizedAnnual = normalizeLegacySolarAccessFactor(explicitAnnual);
  const normalizedMonthly = rawMonthly.map((value) =>
    normalizeLegacySolarAccessFactor(value)
  );
  const validMonthly = normalizedMonthly.filter(
    (value) => value !== null
  );
  const averageMonthly = validMonthly.length
    ? validMonthly.reduce((sum, value) => sum + value, 0) /
      validMonthly.length
    : null;
  const annual = normalizedAnnual ?? averageMonthly;
  const hasValues =
    rawSolarAccess !== undefined || normalizedAnnual !== null;
  const monthly = normalizedMonthly.length
    ? normalizedMonthly.map((value) => value ?? 0)
    : annual !== null
      ? Array(MONTHS_PER_YEAR).fill(annual)
      : [];

  return {
    annual,
    hasValues,
    monthly,
  };
};

const getLegacyRoofMonthlyValues = ({ roof = {}, persistedDesigns = [], roofIndex = 0 } = {}) => {
  const persistedRoof = getPersistedRoof(persistedDesigns, roof?.id, roofIndex);
  const candidates = [
    roof?.acMonthly,
    roof?.ac_monthly,
    persistedRoof?.ac_monthly,
    persistedRoof?.acMonthly,
  ];
  const source =
    candidates.find(
      (value) => normalizeLegacyMonthlyValues(value).length > 0
    ) ?? firstDefined(...candidates);

  return normalizeLegacyMonthlyValues(source);
};

export const hasUsableLegacyPanelShading = (panelShadings = []) =>
  Array.isArray(panelShadings) &&
  panelShadings.some(
    (entry) => Array.isArray(entry) && finiteNumber(entry?.[6]) > 0
  );

export const buildLegacyNoShadingRuntimeDesign = ({
  design = [],
  persistedDesigns = [],
} = {}) => {
  if (!Array.isArray(design)) return [];

  return design.map((roof, roofIndex) => {
    const persistedRoof = getPersistedRoof(
      persistedDesigns,
      roof?.id,
      roofIndex
    );
    const acMonthly = getLegacyRoofMonthlyValues({
      roof,
      persistedDesigns,
      roofIndex,
    });
    const solarAccess = getLegacyRoofSolarAccess({
      roof,
      persistedDesigns,
      roofIndex,
    });
    const panelCount = getLegacyRoofPanelCount(roof);
    const panels = Array.isArray(roof?.panels)
      ? roof.panels.map((panel) => ({ ...panel }))
      : Array(panelCount).fill({});
    const persistedTsrf = normalizeLegacySolarAccessValues(
      persistedRoof?.tsrf
    );
    const annualTsrf = finiteNumber(
      firstDefined(
        roof?.annualTSRF,
        roof?.annual_tsrf,
        persistedRoof?.annual_tsrf,
        persistedRoof?.annualTSRF,
        persistedTsrf[MONTHS_PER_YEAR]
      )
    );

    return {
      ...roof,
      acAnnual: acMonthly.reduce(
        (sum, value) => sum + finiteNumber(value),
        0
      ),
      acMonthly,
      annualAccess: solarAccess.annual ?? 1,
      annualTSRF: annualTsrf,
      panels,
      solarAccess: solarAccess.monthly.length
        ? solarAccess.monthly
        : Array(MONTHS_PER_YEAR).fill(1),
    };
  });
};

export const buildLegacyNoShadingRuntimeSelection = ({
  annualUsage = 0,
  calculateNoShading = null,
  design = [],
  panel = null,
  persistedDesigns = [],
} = {}) => {
  if (typeof calculateNoShading !== 'function') return null;

  const runtimeDesign = buildLegacyNoShadingRuntimeDesign({
    design,
    persistedDesigns,
  });
  const hasPersistedRoofProduction = runtimeDesign.some(
    (roof) =>
      Number(roof?.acAnnual) > 0 && Number(roof?.annualAccess) > 0
  );
  if (!hasPersistedRoofProduction) return null;

  const selection = calculateNoShading(
    panel,
    Number(annualUsage || 0),
    runtimeDesign,
    true
  );
  if (!Array.isArray(selection?.selectedPanels)) return null;

  const selectedPanels = {};
  runtimeDesign.forEach((roof, roofIndex) => {
    selectedPanels[roof.id] = Array.isArray(
      selection.selectedPanels[roofIndex]
    )
      ? selection.selectedPanels[roofIndex]
      : Array(roof.panels.length).fill(0);
  });

  return {
    ...selection,
    selectedPanels,
  };
};

const getLegacySelectedPanelFactors = ({
  roof = {},
  roofSelection = [],
  panelShadings = [],
  persistedDesigns = [],
  roofIndex = 0,
} = {}) => {
  const roofPanelShadings = Array.isArray(panelShadings) ? panelShadings.filter(([idx]) => Number(idx) === roofIndex) : [];
  const hasRoofPanelShadings = roofPanelShadings.length > 0;
  const hasUsableRoofPanelShadings = hasUsableLegacyPanelShading(
    roofPanelShadings
  );
  const roofSolarAccess = getLegacyRoofSolarAccess({
    roof,
    persistedDesigns,
    roofIndex,
  });
  const useRoofSolarAccess =
    !hasUsableRoofPanelShadings && roofSolarAccess.hasValues;
  const roofPanelShadingsByPanelIndex = new Map(
    roofPanelShadings
      .filter((entry) => Number.isInteger(Number(entry?.[2])))
      .map((entry) => [finitePanelCount(entry[2]), entry])
  );
  const usePanelIndexLookup =
    roofPanelShadingsByPanelIndex.size === roofPanelShadings.length;

  const annualFactor = useRoofSolarAccess
    ? roofSolarAccess.annual ?? 0
    : !hasRoofPanelShadings
      ? 1
      : null;

  return {
    annualFactors: (Array.isArray(roofSelection) ? roofSelection : []).map(
      (selected, panelIndex) => {
        if (finiteNumber(selected) <= 0) return 0;
        if (annualFactor !== null) return annualFactor;
        const shadingEntry =
          (usePanelIndexLookup
            ? roofPanelShadingsByPanelIndex.get(panelIndex)
            : null) || roofPanelShadings?.[panelIndex];
        return Math.max(0, finiteNumber(shadingEntry?.[6]));
      }
    ),
    monthlyFactors: useRoofSolarAccess
      ? roofSolarAccess.monthly
      : null,
  };
};

export const buildLegacyProductionPerPanel = ({
  design = [],
  selectedPanels = {},
  panelKw = 0,
  persistedDesigns = [],
  panelShadings = [],
} = {}) => {
  if (!Array.isArray(design) || panelKw <= 0) return [];

  return design.map((roof, roofIndex) => {
    const id = roof?.id;
    const roofPanelCount = getLegacyRoofPanelCount(roof);
    const roofSelection = Array.isArray(selectedPanels?.[id]) ? selectedPanels[id] : [];
    const acMonthly = getLegacyRoofMonthlyValues({
      roof,
      persistedDesigns,
      roofIndex,
    });
    const acAnnual = acMonthly.reduce((sum, value) => sum + finiteNumber(value), 0);
    const { annualFactors } = getLegacySelectedPanelFactors({
      roof,
      roofSelection,
      panelShadings,
      persistedDesigns,
      roofIndex,
    });

    const roofPanels = Array(Math.max(roofPanelCount, annualFactors.length, 0))
      .fill(0)
      .map((_, panelIndex) => {
        const factor = finiteNumber(annualFactors[panelIndex]);
        return factor > 0 ? factor * panelKw * acAnnual : 0;
      });

    return {
      idx: roofIndex,
      roofPanels,
    };
  });
};

export const buildMonthlyProductionFromLegacyDesign = ({
  design = [],
  selectedPanels = {},
  panelKw = 0,
  persistedDesigns = [],
  panelShadings = [],
} = {}) => {
  if (!Array.isArray(design) || panelKw <= 0) return null;

  const monthlyProduction = new Array(MONTHS_PER_YEAR).fill(0);
  let hasProduction = false;

  design.forEach((roof, roofIndex) => {
    const id = roof?.id;
    const roofSelection = Array.isArray(selectedPanels?.[id]) ? selectedPanels[id] : [];
    const selectedCount = roofSelection.reduce((sum, value) => sum + (finiteNumber(value) > 0 ? 1 : 0), 0);
    if (selectedCount <= 0) return;

    const { annualFactors, monthlyFactors } =
      getLegacySelectedPanelFactors({
        roof,
        roofSelection,
        panelShadings,
        persistedDesigns,
        roofIndex,
      });
    const selectedFactorTotal = annualFactors.reduce(
      (sum, factor) => sum + finiteNumber(factor),
      0
    );
    if (selectedFactorTotal <= 0) return;

    const acMonthly = getLegacyRoofMonthlyValues({
      roof,
      persistedDesigns,
      roofIndex,
    });
    acMonthly.slice(0, MONTHS_PER_YEAR).forEach((value, month) => {
      const monthlyFactor = Array.isArray(monthlyFactors)
        ? finiteNumber(monthlyFactors[month]) * selectedCount
        : selectedFactorTotal;
      const production = monthlyFactor * panelKw * finiteNumber(value);
      monthlyProduction[month] += production;
      if (production > 0) hasProduction = true;
    });
  });

  return hasProduction ? monthlyProduction : null;
};

export const buildPersistedLegacyProductionState = ({
  design = [],
  panel = null,
  selectedPanels = {},
  persistedDesigns = [],
  annualUsage = 0,
  fallbackOffset = 0,
  fallbackSystemSize = 0,
  panelShadings = [],
  designData = null,
} = {}) => {
  const panelKw = finiteNumber(panel?.power) / 1000;
  if (!Array.isArray(design) || design.length === 0 || panelKw <= 0) {
    return null;
  }

  const selectionState = normalizeLegacySelectedPanels(design, selectedPanels);
  if (!selectionState) return null;
  const normalizedPanelShadings = normalizeLegacyPanelShadingOrder(panelShadings, {
    design,
    designData,
  });

  const monthlyProduction =
    buildMonthlyProductionFromLegacyDesign({
      design,
      selectedPanels: selectionState.selectedPanels,
      panelKw,
      persistedDesigns,
      panelShadings: normalizedPanelShadings,
    }) || getPersistedMonthlyProduction(persistedDesigns);

  if (!monthlyProduction) return null;

  const productionPerPanelData = buildLegacyProductionPerPanel({
    design,
    selectedPanels: selectionState.selectedPanels,
    panelKw,
    persistedDesigns,
    panelShadings: normalizedPanelShadings,
  });
  const solarProduction = productionPerPanelData.reduce(
    (total, roof) =>
      total +
      (Array.isArray(roof?.roofPanels)
        ? roof.roofPanels.reduce(
          (roofTotal, value) => roofTotal + finiteNumber(value),
          0
        )
        : 0),
    0
  );
  if (solarProduction <= 0) return null;

  const systemSize =
    finiteNumber(fallbackSystemSize) > 0 ? finiteNumber(fallbackSystemSize) : selectionState.totalPanels * panelKw;
  const annualUsageNumber = finiteNumber(annualUsage);
  const offset =
    annualUsageNumber > 0 ? Math.round((solarProduction / annualUsageNumber) * 100) : finiteNumber(fallbackOffset);

  return {
    selectedPanels: selectionState.selectedPanels,
    totalPanels: selectionState.totalPanels,
    systemSize,
    solarProduction,
    monthlyProduction,
    productionPerPanelData,
    offset,
  };
};

export const buildLegacyProductionCallbackPayload = ({
  productionPayload = {},
  annualUsage = 0,
  fallbackOffset = 0,
} = {}) => {
  const annualKwh = finiteNumber(productionPayload?.yearlyProduction);
  const annualUsageNumber = sumAnnualUsage(annualUsage);
  const offset =
    annualUsageNumber > 0
      ? Math.floor((annualKwh / annualUsageNumber) * 100)
      : Math.floor(finiteNumber(productionPayload?.offset, finiteNumber(fallbackOffset)));

  return {
    offset,
    systemSize: finiteNumber(productionPayload?.systemSize).toFixed(3),
    yearlyProduction: annualKwh.toFixed(0),
    monthlyProduction: toLegacyMonthlyCallbackValues(productionPayload?.monthlyProduction),
    numTotalPanels: finitePanelCount(productionPayload?.numTotalPanels),
    numSelectedPanels: finitePanelCount(productionPayload?.numSelectedPanels),
    productionPerPanel: Array.isArray(productionPayload?.productionPerPanel) ? productionPayload.productionPerPanel : [],
    pitchAndAzimuthPerRoof: Array.isArray(productionPayload?.pitchAndAzimuthPerRoof)
      ? productionPayload.pitchAndAzimuthPerRoof
      : [],
    shadingPerRoof: Array.isArray(productionPayload?.shadingPerRoof) ? productionPayload.shadingPerRoof : [],
  };
};

export const buildProposalProductionCallbackPayload = ({
  isLegacyProposal = false,
  productionPayload = {},
  annualUsage = 0,
  fallbackOffset = 0,
} = {}) => {
  if (!isLegacyProposal) return productionPayload;

  return buildLegacyProductionCallbackPayload({
    productionPayload,
    annualUsage,
    fallbackOffset,
  });
};

export const getLegacyCanvasStringingViewProps = ({
  isLegacyProposal = false,
  stringingData = null,
  showStringing = false,
  forceShowStringing = false,
} = {}) => {
  if (isLegacyProposal) {
    return {
      stringingData: [],
      showStringing: false,
      forceShowStringing: false,
    };
  }

  return {
    stringingData: stringingData || [],
    showStringing,
    forceShowStringing,
  };
};
